/* =============================================================================
 * Armazenamento do modo offline - a camada de baixo nivel.
 *
 * Dois depositos, cada um para o que faz melhor:
 *
 *  - IndexedDB (via idb-keyval): os payloads JSON. O localStorage de
 *    shared/lib/armazenamento.js tem teto de ~5MB e guarda preferencia, nao
 *    conteudo - uma competicao inteira nao cabe la'.
 *  - Cache API ('futigol-offline-imagens'): os escudos. Guarda Response nativo,
 *    sem inflar 33% em base64 - e e' o MESMO cache que o service worker
 *    consulta ao servir /file (ver src/sw.js). E' isso que faz a imagem baixada
 *    aparecer na tela sem que nenhum componente saiba que esta' offline.
 *
 * TRES BASES E NAO TRES STORES: `createStore` do idb-keyval cria o object store
 * so' no upgrade da versao 1, entao duas chamadas com o mesmo nome de base e
 * stores diferentes nao funcionam - a segunda abriria a base ja' criada e nao
 * acharia o store dela.
 *
 * REGRA DE OURO: nada aqui pode quebrar a aplicacao. Toda funcao devolve
 * Promise e NUNCA rejeita - em falha devolve null/false/[] e a chamada segue o
 * fluxo normal (que e' ir a' rede). Navegador em modo privativo, cota estourada
 * e IndexedDB bloqueado por outra aba caem todos aqui.
 * ========================================================================== */

import { createStore, get, set, del, entries } from 'idb-keyval';

const PREFIXO_BASE = 'futigol-offline';
export const NOME_CACHE_IMAGENS = 'futigol-offline-imagens';

/**
 * Criacao PREGUICOSA: `createStore` chama indexedDB.open na hora, e o modulo e'
 * importado tambem pelo ambiente de teste (happy-dom), onde indexedDB pode nem
 * existir. Assim o custo - e a falha - so' acontece em quem realmente usa.
 */
function criarDeposito(nome) {
    let store;
    let tentou = false;
    return () => {
        if (tentou) return store;
        tentou = true;
        try {
            store = createStore(`${PREFIXO_BASE}-${nome}`, nome);
        } catch {
            store = null;
        }
        return store;
    };
}

//Um pacote e' uma unidade de conteudo que a pessoa escolheu levar (a navegacao
//basica, uma competicao). Datasets sao os dados consultados pelos resolvedores;
//respostas sao o cache cego, chaveado pela requisicao inteira.
export const PACOTES = criarDeposito('pacotes');
export const DATASETS = criarDeposito('datasets');
export const RESPOSTAS = criarDeposito('respostas');

export function disponivel() {
    try {
        return typeof indexedDB !== 'undefined' && !!indexedDB;
    } catch {
        return false;
    }
}

export function cacheImagensDisponivel() {
    try {
        return typeof caches !== 'undefined' && !!caches;
    } catch {
        return false;
    }
}

/* ---- JSON --------------------------------------------------------------- */

export async function gravar(deposito, chave, valor) {
    const store = deposito();
    if (!store) return false;
    try {
        await set(chave, valor, store);
        return true;
    } catch {
        //Cota estourada cai aqui. Quem chamou precisa SABER: gravar um pacote
        //como concluido depois de uma gravacao que falhou seria prometer
        //conteudo que nao existe.
        return false;
    }
}

export async function obter(deposito, chave) {
    const store = deposito();
    if (!store) return null;
    try {
        return (await get(chave, store)) ?? null;
    } catch {
        return null;
    }
}

export async function excluir(deposito, chave) {
    const store = deposito();
    if (!store) return false;
    try {
        await del(chave, store);
        return true;
    } catch {
        return false;
    }
}

/**
 * Lista os registros, opcionalmente so' os de um contexto.
 *
 * O filtro e' por PREFIXO DA CHAVE, e nao por indice: toda chave do offline
 * comeca pelo contexto (ver offline.js#chaveDe), entao o mesmo aparelho com
 * duas contas nunca mistura conteudo - e listar sem contexto continua sendo o
 * que o diagnostico precisa para mostrar o pacote que ficou orfao.
 */
export async function listar(deposito, contexto = '') {
    const store = deposito();
    if (!store) return [];
    try {
        const todos = await entries(store);
        return todos.filter(([chave]) => !contexto || String(chave).startsWith(`${contexto}|`)).map(([, valor]) => valor);
    } catch {
        return [];
    }
}

/** Apaga tudo o que pertence a um contexto, nos tres depositos. */
export async function limparContexto(contexto) {
    const depositos = [PACOTES, DATASETS, RESPOSTAS];
    for (const dep of depositos) {
        const store = dep();
        if (!store) continue;
        try {
            const todos = await entries(store);
            const alvos = todos.map(([chave]) => chave).filter((chave) => !contexto || String(chave).startsWith(`${contexto}|`));
            await Promise.all(alvos.map((chave) => del(chave, store)));
        } catch {
            /* um deposito que falha nao impede a limpeza dos outros */
        }
    }
    return true;
}

/* ---- imagens (Cache API) ------------------------------------------------ */

async function cacheImagens() {
    if (!cacheImagensDisponivel()) return null;
    try {
        return await caches.open(NOME_CACHE_IMAGENS);
    } catch {
        return null;
    }
}

export async function gravarImagem(url, blob) {
    if (!url || !blob) return false;
    const cache = await cacheImagens();
    if (!cache) return false;
    try {
        const resposta = new Response(blob, {
            headers: {
                'Content-Type': blob.type || 'image/webp',
                'Content-Length': String(blob.size),
                //Marca de origem: e' o que distingue, no DevTools, o que veio do
                //download deliberado do que o service worker cacheou de passagem.
                'X-Futigol-Offline': '1',
            },
        });
        await cache.put(url, resposta);
        return true;
    } catch {
        return false;
    }
}

/**
 * Guarda a resposta como ela veio - inclusive OPACA.
 *
 * O /file da API e' outro dominio e nao devolve cabecalho de CORS: um `<img>`
 * mostra a imagem sem problema (nao precisa ler os bytes), mas o `fetch` do
 * download nao consegue le-la. A saida e' pedir em `no-cors` e guardar a
 * resposta opaca sem abrir: nao da' para reduzir nem medir o tamanho, mas o
 * `<img>` offline a encontra no cache exatamente igual.
 */
export async function gravarRespostaImagem(url, resposta) {
    if (!url || !resposta) return false;
    const cache = await cacheImagens();
    if (!cache) return false;
    try {
        await cache.put(url, resposta);
        return true;
    } catch {
        return false;
    }
}

export async function existeImagem(url) {
    if (!url) return false;
    const cache = await cacheImagens();
    if (!cache) return false;
    try {
        return !!(await cache.match(url));
    } catch {
        return false;
    }
}

export async function excluirImagens(urls) {
    if (!urls?.length) return true;
    const cache = await cacheImagens();
    if (!cache) return true;
    try {
        await Promise.all(urls.map((url) => cache.delete(url).catch(() => false)));
    } catch {
        /* ignorado */
    }
    return true;
}

export async function limparImagens() {
    if (!cacheImagensDisponivel()) return true;
    try {
        return await caches.delete(NOME_CACHE_IMAGENS);
    } catch {
        return false;
    }
}

/* ---- espaco ------------------------------------------------------------- */

/** Devolve sempre um objeto - zeros quando o navegador nao expoe a API. */
export async function estimarArmazenamento() {
    try {
        if (!navigator.storage?.estimate) return { usado: 0, total: 0, livre: 0, disponivel: false };
        const e = await navigator.storage.estimate();
        const usado = e.usage ?? 0;
        const total = e.quota ?? 0;
        return { usado, total, livre: Math.max(0, total - usado), disponivel: true };
    } catch {
        return { usado: 0, total: 0, livre: 0, disponivel: false };
    }
}

/**
 * Pede ao navegador para nao descartar estes dados sob pressao de espaco.
 *
 * Sem isto o conteudo baixado e' "melhor esforco": o Chrome descarta o
 * armazenamento do site quando o disco aperta, e a pessoa descobre no estadio,
 * sem rede. Em PWA instalado costuma ser concedido sem perguntar nada.
 */
export async function solicitarPersistencia() {
    try {
        if (!navigator.storage?.persist) return false;
        if (await navigator.storage.persisted()) return true;
        return await navigator.storage.persist();
    } catch {
        return false;
    }
}
